import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { supabase } from '../services/supabaseClient';
import { ALL_PERMISSIONS } from '../lib/permissions';

// ── Context ───────────────────────────────────────────────────────────────────

const PermissionsContext = createContext({
  permissions: new Set(),
  isAdmin: false,
  loading: true,
  can: () => false,
  reload: () => {},
});

// ── Provider ──────────────────────────────────────────────────────────────────

export const PermissionsProvider = ({ children }) => {
  const { user } = useAuth();

  const [permissions, setPermissions] = useState(new Set());
  const [isAdmin, setIsAdmin]         = useState(false);
  const [loading, setLoading]         = useState(true);

  const load = useCallback(async () => {
    if (!user) {
      setPermissions(new Set());
      setIsAdmin(false);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      // Fetch the user's current role_id (may have changed since last profile load).
      // We filter only by user id — each user has exactly one row, and adding
      // a firm_id filter risks returning null during the brief window when
      // firmId hasn't settled yet (causing a false "no permissions" state).
      const { data: profile } = await supabase
        .from('users')
        .select('role_id, role')
        .eq('id', user.id)
        .maybeSingle();

      if (!profile) {
        setPermissions(new Set());
        setIsAdmin(false);
        return;
      }

      // Admins (either by legacy text role OR by having roles.manage permission)
      // are given all permissions — skip the DB lookup for speed.
      if (profile.role === 'Admin') {
        setPermissions(new Set(ALL_PERMISSIONS));
        setIsAdmin(true);
        return;
      }

      if (!profile.role_id) {
        // No role assigned yet — read-only defaults
        setPermissions(new Set(['cases.view', 'documents.view', 'contacts.view']));
        setIsAdmin(false);
        return;
      }

      // Fetch permissions for this role
      const { data: perms } = await supabase
        .from('firm_role_permissions')
        .select('permission')
        .eq('role_id', profile.role_id);

      const permSet = new Set((perms || []).map(p => p.permission));

      // Check admin via permission (for users migrated away from text 'Admin')
      if (permSet.has('roles.manage')) {
        setPermissions(new Set(ALL_PERMISSIONS));
        setIsAdmin(true);
      } else {
        setPermissions(permSet);
        setIsAdmin(false);
      }
    } catch (err) {
      console.error('PermissionsContext load error:', err);
      // Fail secure: no permissions
      setPermissions(new Set());
      setIsAdmin(false);
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Load on mount and whenever user or firm changes
  useEffect(() => { load(); }, [load]);

  const can = useCallback(
    (permission) => isAdmin || permissions.has(permission),
    [isAdmin, permissions]
  );

  const canAny = useCallback(
    (list) => isAdmin || list.some(p => permissions.has(p)),
    [isAdmin, permissions]
  );

  return (
    <PermissionsContext.Provider value={{ permissions, isAdmin, loading, can, canAny, reload: load }}>
      {children}
    </PermissionsContext.Provider>
  );
};

// ── Hook ──────────────────────────────────────────────────────────────────────

export const usePermissions = () => useContext(PermissionsContext);

// ── Can component ─────────────────────────────────────────────────────────────
// Conditionally renders children based on a permission check.
//
// Usage:
//   <Can permission="cases.delete">
//     <button onClick={deleteCase}>Delete</button>
//   </Can>
//
//   <Can permission="billing.view" fallback={<AccessDenied />}>
//     <BillingPage />
//   </Can>
//
//   <Can any={['cases.edit', 'cases.delete']}>
//     <ActionMenu />
//   </Can>

export const Can = ({ permission, any: anyList, fallback = null, children }) => {
  const { can, canAny, loading } = usePermissions();
  if (loading) return null;
  const allowed = anyList ? canAny(anyList) : can(permission);
  return allowed ? children : fallback;
};

// ── AccessDenied page ─────────────────────────────────────────────────────────
// Drop this in as the fallback prop on Can for full-page guards.

export const AccessDenied = () => (
  <div className="flex flex-col items-center justify-center py-24 gap-3 text-center px-4">
    <div className="w-14 h-14 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
      <svg className="w-7 h-7 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
          d="M12 15v2m0 0v2m0-2h2m-2 0H10m2-6V7m0 0a2 2 0 10-4 0 2 2 0 004 0z" />
      </svg>
    </div>
    <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-200">Access restricted</h2>
    <p className="text-sm text-slate-500 dark:text-slate-400 max-w-xs">
      You don't have permission to view this page. Contact your firm administrator to request access.
    </p>
  </div>
);
